// bluaIQ — TASK-WEB-1: read-only Takeoff display module (step 3 of the website-bridge
// execution roadmap, authorized 2026-07-08).
//
// READ-ONLY ONLY. This module performs NO network I/O (no fetch/apiFetch), makes NO
// POST/PATCH/PUT/DELETE, calls no AI/Anthropic API, writes nothing to any database, and
// creates nothing in any other module. The payload is a static, per-project TypeScript
// fixture loaded at import time (matching the AIDT/PML demo-shell precedent already in
// this repo) — not a runtime fetch. Rendering is fully deterministic.
//
// VALIDATE BEFORE RENDER: the payload is checked against contract v3
// (src/shared/websitePayloadContractV3.ts — the additive amendment over v2 that allows
// plan_qa_report) before anything else renders. On failure, nothing from the payload is
// shown — INCLUDING plan_qa_report — only a bilingual "view unavailable" message, and
// the violations are logged (console.error) for internal debugging only.
//
// TWO VIEWS, DATA-LAYER FILTERING: auditor and client views read the SAME validated
// payload. The client view is derived via deriveClientLines() in the view-model module
// — which actually strips designation/trust_tier/classification from the underlying
// objects — never a CSS hide/show of auditor-only fields.

import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { QuantificationLineSource, QuantificationStatus } from '../../shared/websitePayloadContractV2'
import { validatePayloadV3 } from '../../shared/websitePayloadContractV3'
import type { PlanQaReport, WebsiteTakeoffPayloadV3 } from '../../shared/websitePayloadContractV3'
import TakeoffRealQuantifyFlow from './TakeoffRealQuantifyFlow'
import TakeoffExcelExportButton from './TakeoffExcelExportButton'
import TakeoffClientQuestionSection from './TakeoffClientQuestionSection'
import { validClientQuestionIds } from './takeoffClientQuestion'
import { shortHash } from './takeoffQuantifyClient'
import type { SourceDocumentRef } from './takeoffQuantifyClient'
import {
  clientBlockedPendingQuestionsInline,
  clientBlockingKindCopy,
  clientWarningCopy,
  clientSectionLabel,
  clientSourceBadgeText,
  rewriteClientNote,
  scrubClientNoteTechnicalTokens,
  stripClientTechnicalSlugs,
} from './takeoffClientCopy'
import { takeoffReadonlyDisplayHouses } from './takeoffReadonlyDisplayFixture'
import type { TakeoffDisplayHouse } from './takeoffReadonlyDisplayFixture'
import {
  DEMO_PROJECT_C1500_ID,
  DEMO_PROJECT_C1500_LABEL,
  getDemoProjectC1500Payload,
} from './takeoffReadonlyDemoProject'
import {
  PLAN_QA_STATUS_LABELS,
  REDACTED_AMOUNT_SENTINEL,
  deriveClientLines,
  deriveClientPlanQa,
  deriveBlockedGroups,
  deriveHonestyCounts,
  derivePendingHighlights,
  derivePlanQaValueView,
  displayableQuantity,
  formatCoverageBand,
  formatDesignation,
  getPlanQaReport,
  isPendingStatus,
  noteContainsRedactionMarker,
  parseCoverageBands,
  parseWaterfallNote,
  partitionMepSections,
  pickLabel,
  resolveStatusLabel,
  deriveAmbiguousOptions,
  splitBilingual,
  splitBilingualNote,
  splitBilingualTitle,
  splitNoteAroundRedactionMarker,
  splitNoteAroundSentinel,
} from './takeoffReadonlyDisplayViewModel'
import type { BlockedGroupKey, ClientQuantificationLine, DisplayView, WaterfallBreakdown } from './takeoffReadonlyDisplayViewModel'

const ACCENT = '#00BCD4'

const COPY = {
  title: {
    en: 'Material quantification (for review — not a purchase order)',
    es: 'Cuantificación de materiales (para revisión — no es una orden de compra)',
  },
  previewBadge: { en: 'Preview (test data)', es: 'Vista previa (datos de prueba)' },
  // TASK-WEB-41 (Codex-ordered copy, shipped with the post-70B demo payload
  // re-export). The demo now runs the CURRENT generic engine, so its openings
  // GD1/W1/W3/D5 arrive PENDING instead of auto-confirmed: the naive crosscheck
  // that used to confirm them ("schedule and placed marks match") is gone by
  // design. Pending is the honest state, not a defect — this says so where a
  // reader meets the demo, so nobody reads the engine's honesty as breakage.
  demoEngineNote: {
    en: 'This demo uses the current engine; pending lines are intentional, not errors',
    es: 'Este demo usa el motor actual; las líneas pendientes son intencionales, no errores',
  },
  invalidState: {
    en: 'View not available — the package did not pass validation',
    es: 'Vista no disponible — el paquete no pasó la validación',
  },
  viewAuditor: { en: 'Auditor view', es: 'Vista auditor' },
  viewClient: { en: 'Client view', es: 'Vista cliente' },
  quantity: { en: 'Quantity', es: 'Cantidad' },
  notAvailable: { en: 'Not available', es: 'No disponible' },
  source: { en: 'Source', es: 'Origen' },
  originalLanguage: { en: 'Original language', es: 'Idioma original' },
  notes: { en: 'Notes', es: 'Notas' },
  designation: { en: 'Designation', es: 'Designación' },
  trustTier: { en: 'Trust tier', es: 'Nivel de confianza' },
  classification: { en: 'Classification', es: 'Clasificación' },
  page: { en: 'p.', es: 'p.' },
  // Redaction badge — the copy itself must explain the amount was hidden ON PURPOSE in
  // the web view (Codex correction: it must never read as missing data).
  amountHidden: {
    en: 'Amount hidden on purpose in the web view',
    es: 'Monto oculto a propósito en la vista web',
  },
  inProgress: { en: 'In progress', es: 'En proceso' },
  confirmed: { en: 'Confirmed', es: 'Confirmado' },
  safeNextAction: { en: 'Next safe step', es: 'Siguiente paso seguro' },
  // v4: point-vs-band labeling — the two must be visually distinguishable.
  measuredPoint: { en: 'Current measurement', es: 'Medición actual' },
  coverageBand: { en: 'Coverage band', es: 'Banda de cobertura' },
  // v4: exterior waterfall card.
  gross: { en: 'Gross', es: 'Bruto' },
  exactDeductions: { en: 'Exact deductions', es: 'Deducciones exactas' },
  net: { en: 'Net', es: 'Neto' },
  doorsNotApplied: { en: 'Doors NOT applied', es: 'Puertas NO aplicadas' },
  selectionPending: { en: 'Selection pending', es: 'Selección pendiente' },
  alternatives: { en: 'Material alternatives (no prices)', es: 'Alternativas de material (sin precios)' },
  // v4: MEP guide block — always apart from material quantities (Codex requirement).
  mepBlockTitle: { en: 'Guides & proposals', es: 'Guías y propuestas' },
  mepBlockLabel: {
    en: 'NOT material quantities, NOT ready to order',
    es: 'NO son cantidades de material, NO listas para ordenar',
  },
  // TASK-WEB-6: dual demo + Plan QA + Codex UI corrections.
  notPurchaseOrder: { en: 'Not a purchase order', es: 'No es una orden de compra' },
  houseSelector: { en: 'Project', es: 'Proyecto' },
  planQaBlockTitle: { en: 'Plan review (Plan QA)', es: 'Revisión del plano (Plan QA)' },
  // Positive framing (Codex copy rule): inconsistencies FOUND AND CORRECTED are value
  // delivered — never a system failure, and never a blanket plan approval.
  planQaValue: {
    en: 'Inconsistencies found and corrected add certainty to your quantities.',
    es: 'Las inconsistencias encontradas y corregidas dan más certeza a sus cantidades.',
  },
  planQaBefore: { en: 'Before', es: 'Antes' },
  planQaAfter: { en: 'After', es: 'Después' },
  planQaStatus: { en: 'Status', es: 'Estado' },
  planQaDesignations: { en: 'Designations applied', es: 'Designaciones aplicadas' },
  quantHeading: { en: 'Preliminary quantification', es: 'Cuantificación preliminar' },
  pendingFirst: { en: 'Pending', es: 'Pendientes' },
  // TASK-WEB-8: owner-gated demo project + explicit quantify button. Labels for
  // ownerOnly and quantifyButton come from the owner designations artifact
  // (TAKEOFF commit 10aa3d9, user_facing_label_proposals) verbatim.
  ownerOnly: {
    en: 'Private preview — visible to the owner only',
    es: 'Vista previa privada — visible solo para la propietaria',
  },
  quantifyButton: { en: 'Quantify plans', es: 'Cuantificar planos' },
  demoEmptyState: {
    en: 'Upload your plans and press Quantify',
    es: 'Suba sus planos y presione Cuantificar',
  },
  demoStaticNote: {
    en: 'Demo: the button loads this project’s pre-generated read-only package after contract validation. Live generation from uploaded plans arrives in a later phase.',
    es: 'Demo: el botón carga el paquete de solo lectura pregenerado de este proyecto tras la validación del contrato. La generación en vivo desde planos subidos llega en una fase posterior.',
  },
  // TASK-WEB-9 (Codex rule): raw evidence is LABELED, never runtime-translated.
  originalText: { en: 'Original plan text', es: 'Texto original del plano' },
  // TASK-WEB-16: the REAL project (route id) — live upload → quantify → render.
  realProjectLabel: { en: 'This project (live)', es: 'Este proyecto (en vivo)' },
  uploadNewPlan: { en: 'Upload new plan', es: 'Subir plano nuevo' },
  // TASK-WEB-33: identity chip for the rendered live/rehydrated payload.
  liveDocumentLabel: { en: 'Plan', es: 'Plano' },
  fingerprintLabel: { en: 'fingerprint', es: 'huella' },
  // TASK-WEB-34: determinism audit surface — the FULL source sha256 shown in
  // the AUDITOR view only. The payload is deterministically keyed by this hash
  // (deterministicRunId(projectId, sha) → identical re-quantify), so the full
  // digest lets an auditor confirm byte-identical re-runs without reading tokens.
  auditorSha256Label: { en: 'Source sha256 (determinism key)', es: 'sha256 de origen (clave de determinismo)' },
  // TASK-WEB-36: the sha256 of the PERSISTED PAYLOAD itself (server-computed
  // over its canonical JSON). This is the surface Codex asked for: a
  // byte-identical re-quantify produces the SAME payload ⇒ the SAME hash, so the
  // auditor verifies determinism on the OUTPUT, not just the input document.
  auditorPayloadSha256Label: { en: 'Payload sha256 (output determinism)', es: 'sha256 del payload (determinismo de salida)' },
  // TASK-WEB-36: honesty counter shown above the result (both views). Factual,
  // view-independent counts derived from the raw candidate-only lines — never a
  // claim of completeness, just "how many lines carry a real measurement vs how
  // many the plan blocked us from measuring".
  honestyMeasured: { en: 'measured', es: 'con medida' },
  honestyBlocked: { en: 'blocked', es: 'bloqueadas' },
  // TASK-WEB-38: collapsible "blocked lines by reason" summary. Groups the
  // blocked lines by WHY they're blocked; a count per group; nothing is hidden
  // (the raw lines still render in their sections below).
  blockedGroupsTitle: { en: 'Blocked lines by type', es: 'Líneas bloqueadas por tipo' },
  blockedGroupsSub: {
    en: 'What still needs data before it can be measured. Not an error — the raw lines are below.',
    es: 'Lo que todavía necesita datos antes de poder medirse. No es un error — las líneas están abajo.',
  },
  blockedGroupRoomsNoDims: { en: 'Rooms without dimensions', es: 'Cuartos sin cotas' },
  // TASK-WEB-40: named after the engine's OWN reason text for
  // requires_wall_or_elevation_geometry_not_in_first_read_probe — these are
  // whole trades (drywall/paint/roofing/siding/insulation/framing), not rooms.
  blockedGroupWallGeometry: { en: 'Requires wall/elevation geometry', es: 'Requiere geometría de muros/alzados' },
  blockedGroupScheduleRows: { en: 'Pending schedule rows', es: 'Filas de schedule pendientes' },
  blockedGroupExcluded: { en: 'Excluded candidates', es: 'Candidatos excluidos' },
  blockedGroupOther: { en: 'Other pending', es: 'Otras pendientes' },
  // TASK-WEB-39: the ambiguity's own values are never hidden — the label and the
  // clarification are translated, the VALUES are shown verbatim in both languages.
  ambiguousOptions: { en: 'Possible options — none chosen', es: 'Opciones posibles — ninguna elegida' },
  ambiguousOptionsSub: {
    en: 'The plan supports more than one reading. Each option is listed as-is; the ambiguity is never resolved silently.',
    es: 'El plano permite más de una lectura. Cada opción se lista tal cual; la ambigüedad jamás se resuelve en silencio.',
  },
} as const

// TASK-WEB-38: bucket key → bilingual label. Kept next to COPY so the view-model
// stays label-free (it returns keys + counts only).
const BLOCKED_GROUP_LABEL: Record<BlockedGroupKey, { en: string; es: string }> = {
  rooms_without_dimensions: COPY.blockedGroupRoomsNoDims,
  requires_wall_or_elevation_geometry: COPY.blockedGroupWallGeometry,
  pending_schedule_rows: COPY.blockedGroupScheduleRows,
  excluded_candidates: COPY.blockedGroupExcluded,
  other: COPY.blockedGroupOther,
}

// TASK-WEB-16: selector id for the real project in the route. Its payload is
// NOT static — it arrives from the Step 2 backend after the real quantify flow
// and passes the SAME validatePayloadV3 gate as the demo before rendering.
const REAL_PROJECT_OPTION_ID = 'real-project-live'

// TASK-WEB-8: selectable projects = the owner-authorized C-1500 demo project
// (designation exposure-demo-project-c1500) FIRST, then the two synthetic test
// houses (kept for tests/QA). The demo project starts EMPTY and only shows its
// payload after the explicit "Cuantificar planos" click (designation
// exposure-generation-by-button — nothing automatic).
const DISPLAY_PROJECTS: (TakeoffDisplayHouse & { isDemo?: true })[] = [
  {
    id: DEMO_PROJECT_C1500_ID,
    label: DEMO_PROJECT_C1500_LABEL,
    payload: getDemoProjectC1500Payload(),
    isDemo: true,
  },
  ...takeoffReadonlyDisplayHouses,
]

// Chip styling for the 5 contract-v3 Plan QA statuses: resolved → green,
// confirmed-but-unfinished → amber, awaiting input → blue.
const PLAN_QA_STATUS_CHIP_CLASS: Record<string, string> = {
  resolved_by_plan_revision: 'bg-green-50 text-green-700',
  resolved_by_user_designation: 'bg-green-50 text-green-700',
  label_absent_in_rev2: 'bg-amber-50 text-amber-800',
  label_error_confirmed_resolution_pending_corrected_dimension: 'bg-amber-50 text-amber-800',
  pending: 'bg-blue-50 text-blue-700',
}

// Bilingual, plain-language copy for the 5 mandatory warning enums from contract v2.

const STATUS_CHIP_CLASS: Record<QuantificationStatus, string> = {
  quantity_confirmed_from_plan: 'bg-green-50 text-green-700',
  quantity_reading_in_progress: 'bg-blue-50 text-blue-700',
  quantity_estimated_from_user_budget: 'bg-blue-50 text-blue-700',
  spec_present_quantity_pending: 'bg-blue-50 text-blue-700',
}

function isBilingualLabel(value: unknown): value is { en: string; es: string } {
  return !!value && typeof value === 'object'
    && typeof (value as { en?: unknown }).en === 'string'
    && typeof (value as { es?: unknown }).es === 'string'
}

function RedactedAmountBadge({ isEs }: { isEs: boolean }) {
  const text = isEs ? COPY.amountHidden.es : COPY.amountHidden.en
  return (
    <span
      data-testid="takeoff-readonly-redacted-badge"
      title={text}
      className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-normal break-words max-w-full"
      style={{ overflowWrap: 'anywhere' }}
    >
      {text}
    </span>
  )
}

/** Auditor notes: full marker text on desktop (`sm:inline`), badge on mobile
 *  (`sm:hidden`) — a pure viewport/density concern, not a data-layer decision (the
 *  auditor is always entitled to the full text; only its presentation shrinks). */
function AuditorNoteText({ notes, isEs }: { notes: string; isEs: boolean }) {
  if (!noteContainsRedactionMarker(notes)) return <>{notes}</>
  const segments = splitNoteAroundRedactionMarker(notes)
  return (
    <>
      {segments.map((seg, i) => (
        <span key={i}>
          {seg}
          {i < segments.length - 1 && (
            <>
              <span data-testid="takeoff-readonly-redacted-full-text" className="hidden sm:inline font-[700] text-gray-600">
                [{isEs ? 'monto no mostrado en la vista web' : 'amount not shown in web view'}]
              </span>
              <span className="sm:hidden">
                <RedactedAmountBadge isEs={isEs} />
              </span>
            </>
          )}
        </span>
      ))}
    </>
  )
}

/** Client notes: ALWAYS the compact badge, on every viewport — the client model never
 *  carries the raw marker text at all (stripped in the data layer), so there is no
 *  "full text" branch to render here even on desktop. */
function ClientNoteText({ notes, isEs }: { notes: string; isEs: boolean }) {
  if (!notes.includes(REDACTED_AMOUNT_SENTINEL)) return <>{notes}</>
  const segments = splitNoteAroundSentinel(notes)
  return (
    <>
      {segments.map((seg, i) => (
        <span key={i}>
          {seg}
          {i < segments.length - 1 && <RedactedAmountBadge isEs={isEs} />}
        </span>
      ))}
    </>
  )
}

function StatusChip({
  status, labelsFromPayload, isEs,
}: { status: QuantificationStatus; labelsFromPayload: Record<string, unknown> | undefined; isEs: boolean }) {
  const label = resolveStatusLabel(status, labelsFromPayload)
  return (
    <span
      data-testid="takeoff-readonly-status-chip"
      data-status={status}
      className={`inline-flex items-center rounded-full text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap ${STATUS_CHIP_CLASS[status]}`}
    >
      {pickLabel(label, isEs)}
    </span>
  )
}

function SourceBadges({ sources, isEs, view }: { sources: QuantificationLineSource[]; isEs: boolean; view: DisplayView }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 min-w-0">
      {sources.map((s, idx) => {
        // TASK-WEB-26 (copy pass, client view only): builder-language provenance
        // label instead of the raw evidence_class token; internal pipeline refs
        // (no whitespace) never render — real evidence text stays verbatim.
        // TASK-WEB-44: that was not enough for the v11 paint lane — an unmapped
        // STATE class prettified into jargon, and a human-shaped ref EMBEDDED two
        // slugs, so both reached the client DOM. clientSourceBadgeText applies the
        // curated copy and the generic last-line guard, and returns null when
        // nothing client-safe survives, so we render no pill instead of debris.
        // The auditor branch stays byte-identical to the pre-WEB-26 render.
        const clientText = view === 'client' ? clientSourceBadgeText(s.evidence_class, s.ref, isEs) : null
        if (view === 'client' && clientText === null) return null
        return (
        <span
          key={`${s.evidence_class}-${s.ref}-${idx}`}
          data-testid="takeoff-readonly-source-badge"
          className="inline-flex items-center rounded-full bg-gray-50 text-gray-600 text-[9px] font-[700] px-2 py-0.5 max-w-full whitespace-normal break-words"
          style={{ overflowWrap: 'anywhere' }}
        >
          {view === 'client' ? clientText : `${s.evidence_class}: ${s.ref}`}
          {s.source_page ? ` (${isEs ? COPY.page.es : COPY.page.en} ${s.source_page})` : ''}
          {/* Auditor-only provenance detail. At runtime these keys simply do not exist
              on client-derived sources (stripped in the data layer), so this branch is
              a no-op for the client view even if the view flag were ever wrong. */}
          {view === 'auditor' && s.designation ? ` · ${isEs ? COPY.designation.es : COPY.designation.en}: ${s.designation}` : ''}
          {view === 'auditor' && s.trust_tier ? ` · ${isEs ? COPY.trustTier.es : COPY.trustTier.en}: ${s.trust_tier}` : ''}
          {view === 'auditor' && s.classification ? ` · ${isEs ? COPY.classification.es : COPY.classification.en}: ${s.classification}` : ''}
        </span>
        )
      })}
    </div>
  )
}

/** v4 band chips: the point ("current measurement") and the band ("coverage band") are
 *  DISTINCT chips with distinct styling — solid vs dashed — so they can never be read
 *  as one number (Codex: "medición actual" vs "banda de cobertura" distinguishable). */
function BandChips({
  line, isEs,
}: {
  line: {
    quantity_value: number | null
    quantity_unit: string
    notes: string
    quantification_status: QuantificationStatus
  }
  isEs: boolean
}) {
  const bands = parseCoverageBands(line.notes)
  if (bands.length === 0) return null
  // D-25AGO-02: this chip is literally labelled "current measurement" / "medición
  // actual", so it is the second surface that could present a blocked line's
  // placeholder zero AS a measurement. Same structural predicate as the quantity
  // slot — no chip at all when there is nothing measured to point at, rather than
  // a chip reading "Medición actual: 0 SF".
  const point = displayableQuantity(line)
  return (
    <div data-testid="takeoff-readonly-band-row" className="mt-1.5 flex flex-wrap items-center gap-1.5 min-w-0">
      {point !== null && (
        <span
          data-testid="takeoff-readonly-measured-point-chip"
          className="inline-flex items-center rounded-full text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
          style={{ backgroundColor: '#E0F7FA', color: '#006064' }}
        >
          {isEs ? COPY.measuredPoint.es : COPY.measuredPoint.en}: {point} {line.quantity_unit}
        </span>
      )}
      {bands.map((band, i) => (
        <span
          key={`${band.min}-${band.max}-${band.unit}-${i}`}
          data-testid="takeoff-readonly-coverage-band-chip"
          className="inline-flex items-center rounded-full bg-white text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap text-gray-700"
          style={{ border: '1px dashed #00838F' }}
        >
          {isEs ? COPY.coverageBand.es : COPY.coverageBand.en}: {formatCoverageBand(band)}
        </span>
      ))}
    </div>
  )
}

/** v4 exterior waterfall card: gross → exact deductions → net, structured from the
 *  note text (no chart needed), with "doors NOT applied" and "selection pending"
 *  always visible and the material alternatives listed WITHOUT prices. */
function WaterfallCard({ wf, isEs }: { wf: WaterfallBreakdown; isEs: boolean }) {
  return (
    <div
      data-testid="takeoff-readonly-waterfall-card"
      className="mt-1.5 rounded-md border border-[#D8F6FA] bg-[#F7FDFE] p-2 min-w-0 space-y-1"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] font-[800] text-[#1C1A17]">
        <span data-testid="takeoff-readonly-waterfall-gross">
          {isEs ? COPY.gross.es : COPY.gross.en}: {wf.gross}
        </span>
        <span style={{ color: ACCENT }}>→</span>
        <span data-testid="takeoff-readonly-waterfall-deductions" className="break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
          − {isEs ? COPY.exactDeductions.es : COPY.exactDeductions.en}: {wf.deductions}
        </span>
        <span style={{ color: ACCENT }}>→</span>
        <span data-testid="takeoff-readonly-waterfall-net" className="font-[900]">
          {isEs ? COPY.net.es : COPY.net.en}: {wf.net}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {wf.doorsNotApplied && (
          <span
            data-testid="takeoff-readonly-waterfall-doors-chip"
            className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
          >
            {isEs ? COPY.doorsNotApplied.es : COPY.doorsNotApplied.en}
          </span>
        )}
        {wf.selectionPending && (
          <span
            data-testid="takeoff-readonly-waterfall-selection-chip"
            className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
          >
            {isEs ? COPY.selectionPending.es : COPY.selectionPending.en}
          </span>
        )}
      </div>
      {wf.alternatives.length > 0 && (
        <div data-testid="takeoff-readonly-waterfall-alternatives" className="min-w-0">
          <p className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">
            {isEs ? COPY.alternatives.es : COPY.alternatives.en}
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {wf.alternatives.map((alt) => (
              <li key={alt} className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
                • {alt}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/** TASK-WEB-9: small chip marking raw plan evidence that ships in its original
 *  language — shown whenever the bilingual splitter finds no clear combined
 *  bilingual text (Codex rule: label raw evidence, never translate at runtime). */
function OriginalTextChip({ isEs }: { isEs: boolean }) {
  return (
    <span
      data-testid="takeoff-readonly-original-text-chip"
      className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 text-[9px] font-[700] px-2 py-0.5 whitespace-nowrap align-middle"
    >
      {isEs ? COPY.originalText.es : COPY.originalText.en}
    </span>
  )
}

/** TASK-WEB-7: render-safe Plan QA value. before/after may be a plain string OR a
 *  structured record (dimension records from the real generator). Strings render as
 *  text; objects render as readable key/value rows via the data-layer formatter —
 *  never a direct interpolation, so "[object Object]" can never reach the DOM. */
function PlanQaValue({ value, isEs }: { value: unknown; isEs: boolean }) {
  const view = derivePlanQaValueView(value)
  if (!view) return null
  if (view.kind === 'text') {
    return (
      <>
        {pickLabel(view.text, isEs)}
        {view.original && <> <OriginalTextChip isEs={isEs} /></>}
      </>
    )
  }
  return (
    <span data-testid="takeoff-readonly-planqa-structured-value" className="mt-0.5 block space-y-0.5">
      {view.fields.map((f) => (
        <span
          key={f.key}
          data-testid="takeoff-readonly-planqa-field"
          className="flex flex-wrap items-baseline gap-x-1.5 min-w-0"
        >
          <span className="font-[800] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
            {pickLabel(f.label, isEs)}:
          </span>
          <span className="break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
            {pickLabel(f.value, isEs)}
          </span>
        </span>
      ))}
    </span>
  )
}

/** TASK-WEB-6: Plan QA block — Plan review rendered as its OWN block, always apart
 *  from preliminary quantification (Codex rule), one card per issue with
 *  before/after/status, framed as value delivered ("found and corrected"), never as
 *  a system failure. */
function PlanQaBlock({ report, isEs }: { report: PlanQaReport; isEs: boolean }) {
  return (
    <div
      data-testid="takeoff-readonly-planqa-block"
      className="rounded-xl border border-[#D8F6FA] bg-[#F7FDFE] p-3 space-y-2 min-w-0"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[13px] font-[900] text-[#1C1A17] break-words min-w-0">
          {isEs ? COPY.planQaBlockTitle.es : COPY.planQaBlockTitle.en}
        </h2>
      </div>
      <p data-testid="takeoff-readonly-planqa-headline" className="text-[11px] font-[800] text-[#0B7285] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
        {pickLabel(report.headline, isEs)}
      </p>
      <p className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0">
        {isEs ? COPY.planQaValue.es : COPY.planQaValue.en}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {report.issues.map((issue, idx) => (
          <div
            key={`planqa-${idx}`}
            data-testid="takeoff-readonly-planqa-issue"
            className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0 space-y-1"
          >
            {/* TASK-WEB-9: combined-bilingual titles split to the active language;
                raw evidence titles (real room names) keep the original-text chip. */}
            <p className="text-[11px] font-[900] text-[#1C1A17] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
              {splitBilingual(issue.issue, isEs ? 'es' : 'en').text}
              {splitBilingual(issue.issue, isEs ? 'es' : 'en').isOriginal && <> <OriginalTextChip isEs={isEs} /></>}
            </p>
            <span
              data-testid="takeoff-readonly-planqa-status-chip"
              data-status={issue.status}
              className={`inline-flex items-center rounded-full text-[9px] font-[800] px-2 py-0.5 whitespace-normal break-words max-w-full ${PLAN_QA_STATUS_CHIP_CLASS[issue.status] ?? 'bg-gray-100 text-gray-700'}`}
            >
              {isEs ? COPY.planQaStatus.es : COPY.planQaStatus.en}: {pickLabel(PLAN_QA_STATUS_LABELS[issue.status], isEs)}
            </span>
            {issue.before !== undefined && issue.before !== null && (
              <p className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
                <span className="font-[900]">{isEs ? COPY.planQaBefore.es : COPY.planQaBefore.en}:</span>{' '}
                <PlanQaValue value={issue.before} isEs={isEs} />
              </p>
            )}
            {issue.after !== undefined && issue.after !== null && (
              <p className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
                <span className="font-[900]">{isEs ? COPY.planQaAfter.es : COPY.planQaAfter.en}:</span>{' '}
                <PlanQaValue value={issue.after} isEs={isEs} />
              </p>
            )}
          </div>
        ))}
      </div>
      {report.designations_applied.length > 0 && (
        <div data-testid="takeoff-readonly-planqa-designations" className="min-w-0">
          {/* TASK-WEB-9: designations are the owner's verbatim EVIDENCE — always
              labeled as original plan text, never runtime-translated. */}
          <p className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890] flex flex-wrap items-center gap-1.5">
            {isEs ? COPY.planQaDesignations.es : COPY.planQaDesignations.en}
            <OriginalTextChip isEs={isEs} />
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {report.designations_applied.map((d) => {
              const formatted = formatDesignation(d)
              return (
                <li key={d} className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
                  • <span className="font-[900]">{pickLabel(formatted.label, isEs)}:</span> {formatted.text}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

interface RenderableLine {
  section: string
  item_description: string
  quantity_value: number | null
  quantity_unit: string
  quantification_status: QuantificationStatus
  sources: QuantificationLineSource[]
  notes: string
  /** Codex cielo-abovedado P0: line-blocking contract, rendered ONLY through
   *  curated copy (see BlockedLineReason). Present in both views. */
  client_blocking_kind?: 'plan_missing_information' | 'lane_not_available' | null
  client_question_ids?: string[]
}

/** The per-line blocked reason (Codex cielo-abovedado P0 — the website used to
 *  DROP the line-blocking contract; only the Excel exporter spoke it).
 *  PRECEDENCE mirrors the exporter (v11.7): a line structurally linked to
 *  VALID client questions says "pending N questions" (count = DISTINCT valid
 *  ids — duplicates never inflate it, Codex #473-#2); otherwise the generic
 *  per-partida sentence from clientBlockingKindCopy. Never the raw enum. */
export function BlockedLineReason({
  line, validQuestionIds, isEs,
}: { line: RenderableLine; validQuestionIds: ReadonlyArray<string>; isEs: boolean }) {
  // Array prop (not a Set): props must survive structured serialization — the
  // Playwright CT harness mounts this component across a boundary where a Set
  // arrives as {} (caught by the first CT run of blockedLineReason.ct.tsx).
  const valid = new Set(validQuestionIds)
  const distinctValid = Array.isArray(line.client_question_ids)
    ? [...new Set(line.client_question_ids)].filter((id) => valid.has(id))
    : []
  const text = distinctValid.length > 0
    ? clientBlockedPendingQuestionsInline(distinctValid.length, isEs)
    : clientBlockingKindCopy(line.client_blocking_kind, isEs)
  if (text === null) return null
  return (
    <p
      data-testid="takeoff-readonly-blocked-reason"
      className="mt-1.5 text-[10px] font-[800] text-[#8A6D1A] bg-[#FFF8E6] border border-[#F2E3B3] rounded-lg px-2 py-1 break-words min-w-0"
      style={{ overflowWrap: 'anywhere' }}
    >
      {text}
    </p>
  )
}

export function LineCard({
  line, view, labelsFromPayload, isEs, index, validQuestionIds,
}: { line: RenderableLine; view: DisplayView; labelsFromPayload: Record<string, unknown> | undefined; isEs: boolean; index: number; validQuestionIds: ReadonlyArray<string> }) {
  const waterfall = parseWaterfallNote(line.notes)
  const pendingHighlights = derivePendingHighlights(line)
  // TASK-WEB-9: parsers (waterfall/bands/pendings) read the FULL raw note; the
  // DISPLAYED note text is the active-language half of a combined-bilingual note,
  // or the labeled original when no clear bilingual separator exists.
  // TASK-WEB-39: v7 notes join SEVERAL bilingual segments with "; ", which the
  // whole-string splitBilingual cannot represent (it returned one half of the
  // whole note and stranded the other language's earlier segments — in prod the
  // English baseboard line lost BOTH of its warnings). splitBilingualNote
  // resolves each segment on its own; single-segment notes are unchanged.
  // TASK-WEB-42: the v8 engine embeds internal pipeline vocabulary in note text
  // (C-1600's `estado view-aware: <slug>` parenthetical, Spring Garden's
  // `user_truth:` prefix). Both reached the CLIENT view in prod. The scrub is
  // presentation-only and CLIENT-ONLY: it keeps the note bilingual (so the split
  // below is unchanged), keeps every builder-meaningful engine word, and drops
  // only the internal prefixes/slugs. The AUDITOR passes the raw note through.
  const noteSource = view === 'client' ? scrubClientNoteTechnicalTokens(line.notes) : line.notes
  const noteSplit = splitBilingualNote(noteSource, isEs ? 'es' : 'en')
  // TASK-WEB-44 — the generic last line, applied HERE and only here for notes.
  // It has to run AFTER the bilingual split: at scrub time the note still holds
  // both languages (so a language-specific substitution would be wrong) and the
  // BLOCKED family is still being parsed from the RAW note by rewriteClientNote.
  // Once split, this is the single-language string that actually reaches the
  // DOM, so a slug surviving every curated rule above is caught right before
  // render — substituted with curated copy, or removed cleanly. Never invented.
  // CLIENT ONLY: the auditor's noteSource is line.notes and is not touched.
  const noteDisplay = view === 'client'
    ? { ...noteSplit, text: stripClientTechnicalSlugs(noteSplit.text, isEs) }
    : noteSplit
  // TASK-WEB-39: the AUDITOR shows the note EXACTLY as the engine emitted it.
  // It used to render the splitter's output, so the auditor silently inherited
  // every splitter artifact (in prod, Spanish lost the ambiguity's values). The
  // auditor's contract is raw evidence, so it now reads line.notes verbatim.
  const ambiguousOptions = deriveAmbiguousOptions(line.notes)
  // TASK-WEB-26 (copy pass, client view only): KNOWN single-language system
  // notes render as curated builder copy; anything unmatched keeps today's
  // behavior exactly (bilingual split / original text + chip). Parsers above
  // always read the RAW note — this is display text only.
  const clientNoteRewrite = view === 'client' ? rewriteClientNote(line.notes, isEs) : null
  // TASK-WEB-38: CLIENT-view line title shows only the ACTIVE language. The v6
  // area composer emits nested bilingual titles (`<ES> (es / en) / <EN>`);
  // splitBilingualTitle picks the active-language side and collapses the inner
  // bilingual parenthetical. Single-language technical titles (e.g. "D1 Door
  // slab 30x80") are returned unchanged. The AUDITOR view keeps the raw
  // item_description verbatim; product specs are never runtime-translated.
  const displayTitle = view === 'client'
    ? splitBilingualTitle(line.item_description, isEs ? 'es' : 'en').text
    : line.item_description
  return (
    <div
      data-testid={`takeoff-readonly-line-${index}`}
      className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0"
    >
      {/* TASK-WEB-6 (Codex UI correction): on MOBILE, everything pending renders
          BEFORE the number. Derived in the data layer (derivePendingHighlights),
          shown here as a mobile-only leading row; desktop keeps the normal order. */}
      {pendingHighlights.length > 0 && (
        <div data-testid="takeoff-readonly-pending-first" className="sm:hidden mb-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-[9px] font-[900] uppercase tracking-wide text-amber-800">
            {isEs ? COPY.pendingFirst.es : COPY.pendingFirst.en}:
          </span>
          {pendingHighlights.map((h, i) => (
            <span
              key={`pending-${i}`}
              className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 text-[9px] font-[800] px-2 py-0.5 whitespace-normal break-words max-w-full"
              style={{ overflowWrap: 'anywhere' }}
            >
              {pickLabel(h, isEs)}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4
          className="text-[12px] font-[900] text-[#1C1A17] break-words min-w-0 flex-1"
          style={{ overflowWrap: 'anywhere' }}
        >
          {displayTitle}
        </h4>
        {/* D-25AGO-02: the quantity slot prints a number ONLY when the line has a
            displayable measurement. A BLOCKED line arrives as `quantity_value: 0`
            + `spec_present_quantity_pending`, and rendering that raw zero turned a
            placeholder into a visible measurement claim ("0 SF" on a line the
            engine never measured). It now falls to the SAME unavailable
            presentation a null quantity has always used. A true measured zero
            still renders `0`. Structural decision only — see
            lineHasDisplayableMeasurement. Applies to BOTH views: LineCard is the
            single card both the client and the auditor render. */}
        <span data-testid="takeoff-readonly-quantity" className="text-[11px] font-[900] text-[#1C1A17] whitespace-nowrap">
          {displayableQuantity(line) ?? '—'} {line.quantity_unit}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <StatusChip status={line.quantification_status} labelsFromPayload={labelsFromPayload} isEs={isEs} />
        {isPendingStatus(line.quantification_status) && (
          <span
            data-testid="takeoff-readonly-inprogress-group-badge"
            className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 text-[9px] font-[700] px-2 py-0.5 whitespace-nowrap"
          >
            {isEs ? COPY.inProgress.es : COPY.inProgress.en}
          </span>
        )}
      </div>
      <BlockedLineReason line={line} validQuestionIds={validQuestionIds} isEs={isEs} />
      {/* v4: point-vs-band chips (only when the note carries a coverage band). */}
      <BandChips line={line} isEs={isEs} />
      <SourceBadges sources={line.sources} isEs={isEs} view={view} />
      {/* v4: waterfall lines render the structured card in place of the raw note text —
          the card IS the note, restructured (gross → deductions → net). */}
      {waterfall ? (
        <WaterfallCard wf={waterfall} isEs={isEs} />
      ) : (
        <p
          data-testid="takeoff-readonly-notes"
          className="mt-1.5 text-[10px] font-[700] text-[#6B655C] break-words min-w-0"
          style={{ overflowWrap: 'anywhere' }}
        >
          {isEs ? COPY.notes.es : COPY.notes.en}:{' '}
          {clientNoteRewrite !== null ? (
            clientNoteRewrite
          ) : view === 'auditor' ? (
            <AuditorNoteText notes={line.notes} isEs={isEs} />
          ) : (
            <ClientNoteText notes={noteDisplay.text} isEs={isEs} />
          )}
          {clientNoteRewrite === null && view === 'client' && noteDisplay.isOriginal && <> <OriginalTextChip isEs={isEs} /></>}
        </p>
      )}
      {/* TASK-WEB-39 (Codex rule): an ambiguous room's candidate perimeters are
          ALWAYS listed, in both languages and both views, right next to the
          blocked line. The client may simplify the language around them; it may
          never hide the values, and none of them is ever chosen. */}
      {ambiguousOptions.length > 0 && (
        <div
          data-testid="takeoff-readonly-ambiguous-options"
          data-count={ambiguousOptions.length}
          className="mt-1.5 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5 min-w-0"
        >
          <span className="text-[9px] font-[900] uppercase tracking-wide text-amber-800">
            {isEs ? COPY.ambiguousOptions.es : COPY.ambiguousOptions.en}
          </span>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 min-w-0">
            {ambiguousOptions.map((opt, i) => (
              <span
                key={`opt-${i}`}
                data-testid={`takeoff-readonly-ambiguous-option-${i}`}
                className="inline-flex items-center rounded-full bg-white border border-amber-300 text-amber-900 text-[10px] font-[800] px-2 py-0.5 whitespace-normal break-words max-w-full"
                style={{ overflowWrap: 'anywhere' }}
              >
                {opt}
              </span>
            ))}
          </div>
          <p className="mt-1 text-[9px] font-[700] text-amber-800/90 break-words">
            {isEs ? COPY.ambiguousOptionsSub.es : COPY.ambiguousOptionsSub.en}
          </p>
        </div>
      )}
    </div>
  )
}

function groupBySection<T extends { section: string }>(lines: T[]): [string, T[]][] {
  const order: string[] = []
  const map = new Map<string, T[]>()
  for (const line of lines) {
    if (!map.has(line.section)) { map.set(line.section, []); order.push(line.section) }
    map.get(line.section)!.push(line)
  }
  return order.map((section) => [section, map.get(section)!])
}

/** `payloadOverride` is a HARNESS-ONLY seam (Codex #490 P0, conditional-hook
 *  fix): the valid→invalid→valid transition test drives the REAL component
 *  through re-renders with changing payload validity — impossible through the
 *  UI, whose selectable payloads are all valid. App routes render the shell
 *  with NO props (pinned in the smoke suite); `undefined` keeps today's
 *  behavior byte-for-byte. */
export default function TakeoffReadonlyDisplayShell({ payloadOverride }: { payloadOverride?: WebsiteTakeoffPayloadV3 | null } = {}) {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)

  const [view, setView] = useState<DisplayView>('client')
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const isOpen = (section: string) => openSections[section] !== false // default open
  // TASK-WEB-38: the "blocked lines by type" summary is collapsed by default so
  // it never competes with the quantified result for attention.
  const [blockedGroupsOpen, setBlockedGroupsOpen] = useState(false)
  const toggleSection = (section: string) => setOpenSections((s) => ({ ...s, [section]: !isOpen(section) }))

  // TASK-WEB-16: the REAL project from the route is selectable FIRST. Its
  // payload is NOT static — the real quantify flow (upload → POST /quantify →
  // poll → GET payload) delivers it, and it passes the SAME v3 gate below
  // before anything renders (identical flow to the demo).
  // TASK-WEB-18 (Codex blocker fix): App.tsx declares the project route as
  // `/projects/:projectId/*` — reading `params.id` returned undefined and the
  // shell silently fell back to demo mode on the real route. `params.id` is
  // kept as a fallback for any harness/route that mounts the shell under an
  // `:id` param.
  const params = useParams()
  const routeProjectId = params.projectId ?? params.id
  const [livePayload, setLivePayload] = useState<WebsiteTakeoffPayloadV3 | null>(null)
  // TASK-WEB-33 (Codex NEEDS FIX): the live payload's source-document identity
  // (name + fingerprint) travels with it and renders NEXT TO the result — a
  // rehydrated or explicitly-selected previous payload must never be mistaken
  // for the most recent attempt.
  const [liveDocument, setLiveDocument] = useState<SourceDocumentRef | null>(null)
  // TASK-WEB-36 (Codex blocker): the server-computed sha256 of the PERSISTED
  // payload's canonical JSON — the determinism-verification surface. Rendered
  // in the auditor view alongside the source-PDF hash so an auditor can confirm
  // a byte-identical re-quantify (same payload ⇒ same hash) without extracting
  // tokens. Null when the payload came from a source with no persisted hash
  // (an explicit "previous result" selection off a rejection).
  const [livePayloadSha256, setLivePayloadSha256] = useState<string | null>(null)
  // TASK-WEB-25 (ceremony blocker #4): a user-requested reset must NOT be
  // instantly undone by the mount-time re-hydration WEB-24 introduced. This
  // flag skips hydration for the reset-mount only; it clears when a new
  // payload arrives, and — being in-memory — a reload still re-hydrates the
  // persisted result (an accidental reset never loses anything).
  const [resetRequested, setResetRequested] = useState(false)

  // TASK-WEB-6/8: project selector — real project (route) first, then the demo
  // project and the two synthetic houses.
  const [houseId, setHouseId] = useState(routeProjectId ? REAL_PROJECT_OPTION_ID : DISPLAY_PROJECTS[0].id)
  const isRealSelected = !!routeProjectId && houseId === REAL_PROJECT_OPTION_ID
  const project = DISPLAY_PROJECTS.find((h) => h.id === houseId) ?? DISPLAY_PROJECTS[0]
  const selectedPayload = isRealSelected ? livePayload : project.payload
  const payload = payloadOverride !== undefined ? payloadOverride : selectedPayload
  const planQa = payload ? getPlanQaReport(payload) : null
  // TASK-WEB-8: the demo project shows NOTHING until the explicit quantify click
  // (owner designation exposure-generation-by-button — nothing automatic).
  const [demoQuantified, setDemoQuantified] = useState(false)
  const showEmptyDemoState = !isRealSelected && !!project.isDemo && !demoQuantified
  // TASK-WEB-16: the real project shows the upload → quantify flow until a
  // validated payload arrives from the backend.
  const showRealFlowState = isRealSelected && !payload
  // TASK-WEB-9: safe_next_action arrives combined bilingual from the engine —
  // display only the active language (or the labeled original as fallback).
  const safeNextAction = payload
    ? splitBilingual(payload.safe_next_action, isEs ? 'es' : 'en')
    : { text: '', isOriginal: false }

  // Contract v3 gating: a payload that fails v3 renders NOTHING — including its
  // plan_qa_report (the invalid branch below returns before any block renders).
  const validation = useMemo(() => (payload ? validatePayloadV3(payload) : null), [payload])

  // Valid client_question ids for the blocked-reason precedence — the SAME
  // shared helper the Excel exporter consumes (Codex #490 P0: one validation,
  // two surfaces; malformed/null questions are not link targets and never
  // throw). HOISTED ABOVE every conditional return (Codex #490 P0: a hook
  // after the invalid-payload return changed the hook count when payload
  // validity flipped between renders, which can break the very screen that
  // shows the bilingual error).
  const validQuestionIds = useMemo<ReadonlyArray<string>>(
    () => validClientQuestionIds((payload as { insulation_lane?: unknown } | null)?.insulation_lane),
    [payload],
  )

  if (payload && validation && !validation.valid) {
    // Internal-only log — no network call, no remote logging service.
    // eslint-disable-next-line no-console
    console.error('[TakeoffReadonlyDisplayShell] payload failed contract v3 validation:', validation.violations)
    return (
      <div data-testid="takeoff-readonly-display-shell" className="p-4 sm:p-5 max-w-5xl mx-auto min-w-0">
        <div
          data-testid="takeoff-readonly-invalid-state"
          className="rounded-xl border border-red-300 bg-red-50 px-3 py-3 text-[12px] font-[800] text-red-800"
        >
          {L(COPY.invalidState)}
        </div>
      </div>
    )
  }

  const auditorSections = payload ? groupBySection(payload.quantification_lines_candidate_only) : []
  const clientLines: ClientQuantificationLine[] = payload
    ? deriveClientLines(payload.quantification_lines_candidate_only)
    : []
  const clientSections = groupBySection(clientLines)
  const sections = view === 'auditor' ? auditorSections : clientSections
  // v4: MEP guide sections split off IN THE DATA LAYER — they render in their own
  // block below, never mixed with material quantities (Codex requirement).
  const { material: materialSections, mep: mepSections } = partitionMepSections(sections)
  // TASK-WEB-36: honesty counter — factual measured/blocked counts over the raw
  // candidate-only lines (view-independent; identical in client and auditor).
  const honesty = payload ? deriveHonestyCounts(payload.quantification_lines_candidate_only) : null
  // TASK-WEB-38: blocked lines grouped by reason (view-independent facts). Only
  // non-empty buckets; the counts sum to honesty.blocked.
  const blockedGroups = payload ? deriveBlockedGroups(payload.quantification_lines_candidate_only) : []
  const originalLanguageLabel = payload && isBilingualLabel(payload.labels.plan_text_original_language)
    ? payload.labels.plan_text_original_language
    : null

  return (
    <div data-testid="takeoff-readonly-display-shell" className="p-4 sm:p-5 max-w-5xl mx-auto min-w-0 space-y-4">
      {/* TASK-WEB-6 (Codex UI correction): persistent "Not a purchase order" badge —
          sticky, visible at ALL times in both views and both houses. */}
      <div
        data-testid="takeoff-readonly-not-purchase-order-badge"
        className="sticky top-0 z-20 rounded-lg px-3 py-1.5 text-center text-[11px] font-[900] uppercase tracking-wide text-white"
        style={{ backgroundColor: '#B4453C' }}
      >
        {L(COPY.notPurchaseOrder)}
      </div>

      {/* Fixed header — identical wording in both views. */}
      <div data-testid="takeoff-readonly-header" className="flex flex-wrap items-center gap-2">
        <h1 className="text-[15px] font-[900] text-[#1C1A17] break-words flex-1 min-w-0">{L(COPY.title)}</h1>
        {/* TASK-WEB-35: the "test data" preview badge is HONEST — it renders only
            for the demo/synthetic projects (static fixture payloads), never for a
            real project (whose payload is the owner's own live quantification, not
            test data). A real project already carries its own candidate-only
            warnings + document identity chip; labelling it "test data" was
            misleading dev-era copy. */}
        {!isRealSelected && (
          <span
            data-testid="takeoff-readonly-preview-badge"
            className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[9px] font-[900] uppercase tracking-wide px-2 py-0.5 whitespace-nowrap"
          >
            {L(COPY.previewBadge)}
          </span>
        )}
        {/* TASK-WEB-8: owner-only chip (designation exposure-access-owner-only). */}
        <span
          data-testid="takeoff-readonly-owner-only-chip"
          className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-normal break-words max-w-full"
          style={{ overflowWrap: 'anywhere' }}
        >
          {L(COPY.ownerOnly)}
        </span>
      </div>

      {/* TASK-WEB-41: Codex-ordered note, shown ONLY next to the C-1500 demo
          project (never on the real route project, never on the synthetic
          houses). It explains why the post-70B demo shows PENDING openings —
          the current engine no longer auto-confirms them — so a reader does not
          mistake intentional honesty for a broken demo. Display only. */}
      {!isRealSelected && project.isDemo && (
        <p
          data-testid="takeoff-readonly-demo-engine-note"
          className="text-[11px] leading-snug text-[#6B655E] whitespace-normal break-words max-w-full"
          style={{ overflowWrap: 'anywhere' }}
        >
          {L(COPY.demoEngineNote)}
        </p>
      )}

      {/* TASK-WEB-6/8/16: project selector — the REAL route project first (live
          flow), then the demo project + the two synthetic houses. */}
      <div data-testid="takeoff-readonly-house-selector" className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-[900] uppercase tracking-wide text-[#9E9890]">{L(COPY.houseSelector)}:</span>
        <div className="inline-flex flex-wrap rounded-lg border border-[#E4EDF6] overflow-hidden">
          {routeProjectId && (
            <button
              type="button"
              data-testid={`takeoff-readonly-house-toggle-${REAL_PROJECT_OPTION_ID}`}
              onClick={() => setHouseId(REAL_PROJECT_OPTION_ID)}
              className="px-3 py-1.5 text-[11px] font-[800] min-w-0 break-words"
              style={isRealSelected ? { backgroundColor: ACCENT, color: '#fff' } : { backgroundColor: '#fff', color: '#1C1A17' }}
            >
              {L(COPY.realProjectLabel)}
            </button>
          )}
          {DISPLAY_PROJECTS.map((h) => (
            <button
              key={h.id}
              type="button"
              data-testid={`takeoff-readonly-house-toggle-${h.id}`}
              onClick={() => setHouseId(h.id)}
              className="px-3 py-1.5 text-[11px] font-[800] min-w-0 break-words"
              style={!isRealSelected && houseId === h.id ? { backgroundColor: ACCENT, color: '#fff' } : { backgroundColor: '#fff', color: '#1C1A17' }}
            >
              {pickLabel(h.label, isEs)}
            </button>
          ))}
        </div>
        {/* TASK-WEB-16: after a live render, a new upload starts a fresh flow. */}
        {isRealSelected && payload && (
          <button
            type="button"
            data-testid="takeoff-quantify-reset-button"
            onClick={() => { setLivePayload(null); setLiveDocument(null); setLivePayloadSha256(null); setResetRequested(true) }}
            className="rounded-lg px-3 py-1.5 text-[11px] font-[800] border"
            style={{ borderColor: ACCENT, color: '#1C1A17' }}
          >
            {L(COPY.uploadNewPlan)}
          </button>
        )}
        {/* TASK-WEB-33 (Codex NEEDS FIX): every live/rehydrated result carries
            its source document's identity (name + fingerprint) — a previous
            payload can never be mistaken for the plan just attempted. */}
        {isRealSelected && payload && liveDocument && (
          <span
            data-testid="takeoff-live-document-chip"
            className="inline-flex max-w-full items-center rounded-full border border-[#D8F6FA] bg-[#F0FCFE] px-2.5 py-1 text-[10px] font-[800] text-[#0B7285] whitespace-normal break-words"
            style={{ overflowWrap: 'anywhere' }}
          >
            {L(COPY.liveDocumentLabel)}: {liveDocument.originalFilename ?? '—'}
            {liveDocument.sourceDocumentHash ? ` · ${L(COPY.fingerprintLabel)} ${shortHash(liveDocument.sourceDocumentHash)}` : ''}
          </span>
        )}
        {/* TASK-WEB-34: determinism audit surface — the FULL 64-char source
            sha256 (INPUT hash). AUDITOR VIEW ONLY, next to the document chip.
            Real quantified payloads only (fixtures carry no source hash).
            Renamed testid in WEB-36: this chip is the SOURCE hash, so its
            data-testid is now honestly `...-source-sha256` (the
            `...-payload-sha256` testid moves to the real payload-content hash
            chip below). */}
        {view === 'auditor' && isRealSelected && payload && liveDocument?.sourceDocumentHash && (
          <span
            data-testid="takeoff-auditor-source-sha256"
            data-sha256={liveDocument.sourceDocumentHash}
            className="inline-flex max-w-full items-center rounded-md border border-[#E4E2DD] bg-[#FAFAF8] px-2 py-1 text-[9px] font-[700] text-[#6B655C] whitespace-normal break-all font-mono"
            style={{ overflowWrap: 'anywhere' }}
          >
            {L(COPY.auditorSha256Label)}: {liveDocument.sourceDocumentHash}
          </span>
        )}
        {/* TASK-WEB-36 (Codex blocker): the sha256 of the PERSISTED PAYLOAD
            (server-computed over canonical JSON, arriving on GET
            /quantify/payload → payloadSha256). AUDITOR VIEW ONLY. This is the
            OUTPUT-determinism surface: a byte-identical re-quantify yields the
            same payload ⇒ the same hash, so an auditor verifies the result is
            deterministic without extracting any tokens. Real quantified payloads
            only (rehydrated/selected payloads with no persisted hash omit it). */}
        {view === 'auditor' && isRealSelected && payload && livePayloadSha256 && (
          <span
            data-testid="takeoff-auditor-payload-sha256"
            data-sha256={livePayloadSha256}
            className="inline-flex max-w-full items-center rounded-md border border-[#D8F6FA] bg-[#F0FCFE] px-2 py-1 text-[9px] font-[700] text-[#0B7285] whitespace-normal break-all font-mono"
            style={{ overflowWrap: 'anywhere' }}
          >
            {L(COPY.auditorPayloadSha256Label)}: {livePayloadSha256}
          </span>
        )}
      </div>

      {/* TASK-WEB-16: REAL quantify flow (upload → button → poll → terminal).
          On done, the fetched payload lands in livePayload and re-enters this
          component through the SAME validatePayloadV3 gate as the demo. */}
      {showRealFlowState && routeProjectId && (
        <TakeoffRealQuantifyFlow
          projectId={routeProjectId}
          isEs={isEs}
          skipHydration={resetRequested}
          onValidatedPayload={(p, doc, payloadSha256) => {
            setLivePayload(p as WebsiteTakeoffPayloadV3)
            setLiveDocument((doc as SourceDocumentRef | null | undefined) ?? null)
            setLivePayloadSha256((payloadSha256 as string | null | undefined) ?? null)
            setResetRequested(false)
          }}
        />
      )}

      {/* TASK-WEB-8: the demo project renders an EMPTY state until the explicit
          "Cuantificar planos" click. The click shows the statically pre-generated,
          v3-validated payload (the validation memo above IS the v3 check — an
          invalid payload never reaches this branch). Live engine generation from
          uploaded plans is a documented later phase. */}
      {showEmptyDemoState && (
        <div
          data-testid="takeoff-readonly-demo-empty-state"
          className="rounded-xl border border-[#E4EDF6] bg-white p-6 min-w-0 flex flex-col items-center gap-3 text-center"
        >
          <p className="text-[12px] font-[800] text-[#6B655C] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
            {L(COPY.demoEmptyState)}
          </p>
          <button
            type="button"
            data-testid="takeoff-readonly-quantify-button"
            onClick={() => { if (validation.valid) setDemoQuantified(true) }}
            className="rounded-lg px-4 py-2 text-[12px] font-[900] text-white"
            style={{ backgroundColor: ACCENT }}
          >
            {L(COPY.quantifyButton)}
          </button>
          <p className="text-[9px] font-[700] text-[#9E9890] break-words min-w-0 max-w-md" style={{ overflowWrap: 'anywhere' }}>
            {L(COPY.demoStaticNote)}
          </p>
        </div>
      )}

      {!showEmptyDemoState && !showRealFlowState && payload && (
        <>
      {/* EXCEL EXPORT (owner-only). Three conditions must ALL hold:
            view === 'auditor'  — the client preview stays clean; "nobody else
                                  sees it" is read strictly, so the button is
                                  absent from the view that represents the client
            isRealSelected      — never on the demo payload; there is no project
                                  to export and no owner to check
            routeProjectId      — the export is project-scoped by definition
          The SERVER is the real boundary (owner-gated, 403/404); this gate is
          about what is SHOWN, not what is permitted. */}
      {view === 'auditor' && isRealSelected && routeProjectId && (
        <TakeoffExcelExportButton projectId={routeProjectId} isEs={isEs} />
      )}
      {/* TASK-WEB-6: Plan QA renders FIRST, as its own block — never mixed with the
          preliminary-quantity blocks below (Codex rule). TASK-WEB-12: the CLIENT
          view receives the data-layer-derived report WITHOUT designations_applied
          (owner-internal ids are auditor-only). */}
      {planQa && <PlanQaBlock report={view === 'client' ? deriveClientPlanQa(planQa) : planQa} isEs={isEs} />}

      {/* Preliminary quantification — everything below this heading is quantity UI. */}
      <h2
        data-testid="takeoff-readonly-quant-heading"
        className="text-[13px] font-[900] text-[#1C1A17] break-words min-w-0"
      >
        {L(COPY.quantHeading)}
      </h2>

      {/* Mandatory warnings banner — plain language, both views. */}
      <div
        data-testid="takeoff-readonly-warning-banner"
        className="rounded-xl border px-3 py-3 min-w-0"
        style={{ borderColor: ACCENT, backgroundColor: '#F0FCFE' }}
      >
        <ul className="space-y-0.5 text-[11px] font-[700] text-[#0B7285]">
          {/* An uncurated warning enum is OMITTED, never rendered raw. The old
              `: w` fallback printed the engine slug (e.g. `candidate_only_not_
              purchase_ready`) straight into the client DOM — the exact defect
              class the 390 QA caught in the Excel export, on the higher-traffic
              surface. Mirrors the exporter: omit here; missingClientCopyEnums
              fails CI so a new enum gets copy instead of shipping raw. */}
          {payload.warnings
            .map((w) => ({ w, copy: clientWarningCopy(w, isEs) }))
            .filter((x): x is { w: string; copy: string } => x.copy !== null)
            .map(({ w, copy }) => (
              <li key={w} data-testid={`takeoff-readonly-warning-${w}`}>
                • {copy}
              </li>
            ))}
        </ul>
      </div>

      {/* client_question — FLAG-GATED (default-OFF, per-account, server-owned).
          Renders in BOTH views (Codex #467 correction 3): the CLIENT view shows
          the friendly cards, the AUDITOR view shows the raw channel
          (source_user_truth / linked_blocking_reason verbatim). The section
          renders nothing unless the server allowlists this account; today no
          live/demo payload carries an insulation_lane block, so it stays dormant
          even when flipped ON. The card's safety is independent of the flag. */}
      <TakeoffClientQuestionSection
        projectId={routeProjectId}
        insulationLane={payload.insulation_lane}
        isEs={isEs}
        view={view}
      />

      {/* View toggle — auditor / client. Both read the same validated payload; the
          client model is derived (data layer), never CSS-hidden. */}
      <div data-testid="takeoff-readonly-view-toggle" className="inline-flex rounded-lg border border-[#E4EDF6] overflow-hidden">
        <button
          type="button"
          data-testid="takeoff-readonly-view-toggle-client"
          onClick={() => setView('client')}
          className="px-3 py-1.5 text-[11px] font-[800]"
          style={view === 'client' ? { backgroundColor: ACCENT, color: '#fff' } : { backgroundColor: '#fff', color: '#1C1A17' }}
        >
          {L(COPY.viewClient)}
        </button>
        <button
          type="button"
          data-testid="takeoff-readonly-view-toggle-auditor"
          onClick={() => setView('auditor')}
          className="px-3 py-1.5 text-[11px] font-[800]"
          style={view === 'auditor' ? { backgroundColor: ACCENT, color: '#fff' } : { backgroundColor: '#fff', color: '#1C1A17' }}
        >
          {L(COPY.viewAuditor)}
        </button>
      </div>

      {originalLanguageLabel && (
        <div
          data-testid="takeoff-readonly-original-language-note"
          className="inline-flex max-w-full items-start rounded-full border border-[#D8F6FA] bg-[#F0FCFE] px-2.5 py-1 text-[10px] font-[800] text-[#0B7285] whitespace-normal break-words"
          style={{ overflowWrap: 'anywhere' }}
        >
          <span className="font-[900]">{isEs ? COPY.originalLanguage.es : COPY.originalLanguage.en}:</span>
          <span className="ml-1">{pickLabel(originalLanguageLabel, isEs)}</span>
        </div>
      )}

      {/* TASK-WEB-36: honesty counter — above the result, both views. Facts, not
          a completeness claim: how many candidate lines carry a real measurement
          vs how many the plan blocked us from measuring. Data-driven from the raw
          payload; the numbers are exposed on data-* for the auditor/tests. */}
      {honesty && (
        <div
          data-testid="takeoff-readonly-honesty-counter"
          data-measured={honesty.measured}
          data-blocked={honesty.blocked}
          data-total={honesty.total}
          className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#E4E2DD] bg-[#FAFAF8] px-3 py-1 text-[11px] font-[800] text-[#1C1A17] whitespace-normal break-words"
          style={{ overflowWrap: 'anywhere' }}
        >
          <span data-testid="takeoff-readonly-honesty-measured" className="text-[#0B7285]">
            {honesty.measured} {L(COPY.honestyMeasured)}
          </span>
          <span aria-hidden="true" className="text-[#B7B2A8]">/</span>
          <span data-testid="takeoff-readonly-honesty-blocked" className="text-[#8A6D3B]">
            {honesty.blocked} {L(COPY.honestyBlocked)}
          </span>
        </div>
      )}

      {/* TASK-WEB-38: collapsible "blocked lines by type" summary — a count per
          reason bucket. Presentation only: nothing is hidden or moved, the raw
          blocked lines still render in their sections below. Both views. */}
      {blockedGroups.length > 0 && (
        <div
          data-testid="takeoff-readonly-blocked-groups"
          className="rounded-xl border border-[#E4E2DD] bg-[#FAFAF8] min-w-0"
        >
          <button
            type="button"
            data-testid="takeoff-readonly-blocked-groups-toggle"
            aria-expanded={blockedGroupsOpen}
            onClick={() => setBlockedGroupsOpen((o) => !o)}
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
          >
            <span className="text-[11px] font-[900] text-[#1C1A17] break-words min-w-0">
              {L(COPY.blockedGroupsTitle)}
            </span>
            <span className="text-[10px] font-[800] text-[#8A6D3B] whitespace-nowrap">
              {blockedGroups.reduce((n, g) => n + g.count, 0)} {L(COPY.honestyBlocked)}{' '}
              <span aria-hidden="true">{blockedGroupsOpen ? '▾' : '▸'}</span>
            </span>
          </button>
          {blockedGroupsOpen && (
            <div data-testid="takeoff-readonly-blocked-groups-body" className="px-3 pb-2 space-y-1">
              <p className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0">
                {L(COPY.blockedGroupsSub)}
              </p>
              {blockedGroups.map((g) => (
                <div
                  key={g.key}
                  data-testid={`takeoff-readonly-blocked-group-${g.key}`}
                  data-count={g.count}
                  className="flex items-center justify-between gap-2 text-[11px] font-[700] text-[#1C1A17] min-w-0"
                >
                  <span className="break-words min-w-0">{L(BLOCKED_GROUP_LABEL[g.key])}</span>
                  <span className="font-[900] text-[#8A6D3B] whitespace-nowrap">{g.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Collapsible MATERIAL sections — MEP guide sections never render here. */}
      <div className="space-y-3">
        {materialSections.map(([section, lines]) => (
          <section key={section} data-testid={`takeoff-readonly-section-${section}`} className="space-y-2">
            <button
              type="button"
              data-testid={`takeoff-readonly-section-toggle-${section}`}
              onClick={() => toggleSection(section)}
              className="w-full flex items-center justify-between gap-2 text-[12px] font-[900] uppercase tracking-wide text-[#1C1A17]"
            >
              {/* TASK-WEB-30: client view shows the active-language header only
                  (curated map, honest raw fallback); auditor keeps the raw
                  combined header. `section` stays the raw grouping/test/toggle
                  key — only the displayed text changes. */}
              <span className="min-w-0 break-words" style={{ overflowWrap: 'anywhere' }}>
                {view === 'client' ? clientSectionLabel(section, isEs) : section}
              </span>
              <span style={{ color: ACCENT }}>{isOpen(section) ? '▾' : '▸'}</span>
            </button>
            {isOpen(section) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lines.map((line, idx) => (
                  <LineCard
                    key={`${section}-${idx}`}
                    line={line}
                    view={view}
                    labelsFromPayload={payload.labels}
                    isEs={isEs}
                    index={idx}
                    validQuestionIds={validQuestionIds}
                  />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      {/* v4: MEP guide/proposal block — a visually SEPARATE block under its own
          heading, never next to material quantities (Codex requirement), with the
          not-material-quantities label always visible. */}
      {mepSections.length > 0 && (
        <div
          data-testid="takeoff-readonly-mep-block"
          className="rounded-xl border border-[#E4EDF6] bg-[#FAFBFD] p-3 space-y-3 min-w-0"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[13px] font-[900] text-[#1C1A17] break-words min-w-0">
              {L(COPY.mepBlockTitle)}
            </h2>
            <span
              data-testid="takeoff-readonly-mep-block-label"
              className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 text-[9px] font-[900] uppercase tracking-wide px-2 py-0.5 whitespace-normal break-words max-w-full"
              style={{ overflowWrap: 'anywhere' }}
            >
              {L(COPY.mepBlockLabel)}
            </span>
          </div>
          {mepSections.map(([section, lines]) => (
            <section key={section} data-testid={`takeoff-readonly-section-${section}`} className="space-y-2">
              <button
                type="button"
                data-testid={`takeoff-readonly-section-toggle-${section}`}
                onClick={() => toggleSection(section)}
                className="w-full flex items-center justify-between gap-2 text-[12px] font-[900] uppercase tracking-wide text-[#1C1A17]"
              >
                {/* TASK-WEB-30: client view shows the active-language header only
                  (curated map, honest raw fallback); auditor keeps the raw
                  combined header. `section` stays the raw grouping/test/toggle
                  key — only the displayed text changes. */}
              <span className="min-w-0 break-words" style={{ overflowWrap: 'anywhere' }}>
                {view === 'client' ? clientSectionLabel(section, isEs) : section}
              </span>
                <span style={{ color: ACCENT }}>{isOpen(section) ? '▾' : '▸'}</span>
              </button>
              {isOpen(section) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {lines.map((line, idx) => (
                    <LineCard
                      key={`${section}-${idx}`}
                      line={line}
                      view={view}
                      labelsFromPayload={payload.labels}
                      isEs={isEs}
                      index={idx}
                      validQuestionIds={validQuestionIds}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {/* Safe next action — never a transactional next step; always a review step.
          TASK-WEB-9: title and content live in SEPARATE block elements (heading
          can never run into the text), and the content is the active-language
          half of the engine's combined-bilingual string. */}
      <div data-testid="takeoff-readonly-safe-next-action" className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
        <h2 data-testid="takeoff-readonly-safe-next-action-title" className="block text-[11px] font-[900] uppercase tracking-wide text-[#9E9890] mb-1.5">{L(COPY.safeNextAction)}</h2>
        <p
          data-testid="takeoff-readonly-safe-next-action-text"
          className="block text-[10px] font-[700] text-[#6B655C] break-words"
          style={{ overflowWrap: 'anywhere' }}
        >
          {safeNextAction.text}
          {safeNextAction.isOriginal && <> <OriginalTextChip isEs={isEs} /></>}
        </p>
      </div>
        </>
      )}
    </div>
  )
}
